import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calculator, AlertTriangle, CheckCircle, Pencil, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${''}/api`;

const TYPE_LABELS = {
  consultation: 'Consultations & visites',
  majoration: 'Majorations forfaitaires',
  deplacement: 'Déplacement (visite à domicile)',
  urgence: 'Urgence cabinet (garde)',
  garde: 'Garde régulée PDSA',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

export const CotationAdminPanel = () => {
  const [tarifs, setTarifs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/cotation/status`);
      setTarifs(res.data.tarifs || []);
      setMeta(res.data.meta || null);
    } catch (e) {
      toast.error('Erreur chargement des tarifs de cotation');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveTarif = async (code) => {
    const val = parseFloat(String(editValue).replace(',', '.'));
    if (Number.isNaN(val) || val < 0) {
      toast.error('Tarif invalide');
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/cotation/ngap/${code}`, { tarif: val });
      toast.success(`${code} mis à jour`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur mise à jour tarif');
    } finally {
      setBusy(false);
    }
  };

  const markVerified = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/cotation/verify`);
      setMeta(res.data.meta || meta);
      toast.success('Tarifs NGAP validés — merci !');
    } catch (e) {
      toast.error('Erreur validation');
    } finally {
      setBusy(false);
    }
  };

  const needsReview = meta?.ngap_needs_review;
  const grouped = tarifs.reduce((acc, t) => {
    (acc[t.type] = acc[t.type] || []).push(t);
    return acc;
  }, {});

  return (
    <div data-testid="cotation-admin-panel" className={`rounded-2xl border ${needsReview ? 'border-amber-500/50 bg-amber-950/10' : 'border-slate-700 bg-slate-900/40'} p-4`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3"
        data-testid="cotation-admin-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center shrink-0">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div className="text-left min-w-0">
            <h3 className="text-white font-semibold flex items-center gap-2">
              Tarifs & Cotation (NGAP)
              {needsReview ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
                  <AlertTriangle className="w-3 h-3" /> À revérifier
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                  <CheckCircle className="w-3 h-3" /> À jour
                </span>
              )}
            </h3>
            <p className="text-xs text-slate-400 truncate">
              {meta ? `${meta.ngap_version} • ${meta.ccam_version}` : 'Source unique des tarifs NGAP + CCAM'}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {/* Metadata + verify */}
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400 bg-slate-800/40 rounded-xl p-3">
            <div className="space-y-0.5">
              <p>Dernière validation : <span className="text-slate-200">{fmtDate(meta?.ngap_last_review)}</span></p>
              <p>Prochaine révision (semestrielle) : <span className="text-slate-200">{fmtDate(meta?.ngap_next_review)}</span></p>
              {meta?.ccam_refresh_status && (
                <p>Dernier import CCAM auto : {meta.ccam_refresh_status.ok
                  ? <span className="text-emerald-400">OK ({meta.ccam_refresh_status.actes_count} actes)</span>
                  : <span className="text-amber-400">échec — vérifier manuellement</span>}</p>
              )}
            </div>
            <button
              onClick={markVerified}
              disabled={busy}
              data-testid="cotation-verify-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Marquer comme vérifié
            </button>
          </div>

          {needsReview && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
              La tâche semestrielle a marqué les tarifs NGAP « à revérifier ». Comparez-les à la dernière circulaire
              officielle Ameli, corrigez si besoin, puis cliquez « Marquer comme vérifié ».
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
            </div>
          ) : (
            Object.keys(TYPE_LABELS).filter((t) => grouped[t]?.length).map((type) => (
              <div key={type}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{TYPE_LABELS[type]}</h4>
                <div className="space-y-1">
                  {grouped[type].map((t) => (
                    <div key={t.code} className="flex items-center gap-3 bg-slate-800/40 rounded-lg px-3 py-2">
                      <span className="font-mono text-green-400 text-sm w-16 shrink-0">{t.code}</span>
                      <span className="text-slate-300 text-sm flex-1 min-w-0 truncate">{t.libelle}</span>
                      {editing === t.code ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <input
                            type="number"
                            step="0.01"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-20 bg-slate-900 border border-slate-600 rounded-lg px-2 py-1 text-sm text-white"
                            data-testid={`cotation-edit-input-${t.code}`}
                          />
                          <button
                            onClick={() => saveTarif(t.code)}
                            disabled={busy}
                            className="px-2 py-1 rounded-lg bg-emerald-600 text-white text-xs"
                            data-testid={`cotation-save-${t.code}`}
                          >OK</button>
                          <button onClick={() => setEditing(null)} className="px-2 py-1 rounded-lg bg-slate-700 text-slate-300 text-xs">✕</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-emerald-400 font-medium text-sm whitespace-nowrap">{t.tarif.toFixed(2).replace('.', ',')} €</span>
                          <button
                            onClick={() => { setEditing(t.code); setEditValue(String(t.tarif)); }}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700"
                            aria-label={`Modifier ${t.code}`}
                            data-testid={`cotation-edit-${t.code}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
